import React, { useState } from 'react';
import useElapsedTimer from '../hooks/useElapsedTimer';

const RESULT_LABEL = { pass: 'P', fail: 'F', mixed_fail: 'F', blank: '·' };
const RESULT_COLOR = {
  pass: '#276749',
  fail: '#c53030',
  mixed_fail: '#c05621',
  blank: '#a0aec0'
};
const RESULT_BG = {
  pass: '#c6f6d5',
  fail: '#fed7d7',
  mixed_fail: '#feebc8',
  blank: '#edf2f7'
};

export default function CompanyCard({
  company,
  isDragging,
  onDragStart,
  onDragEnd,
  style,
  stagingEnteredAt,
  isRecommended,
  stations,
  requiredStations,
  onBeginMakeups,
  onMoveToPassed,
  onDispatchToStation
}) {
  const [makeupExpanded, setMakeupExpanded] = useState(false);
  const candidateNames = company.candidate_names ? company.candidate_names.join('/') : '';
  const stagingElapsed = useElapsedTimer(stagingEnteredAt || null);

  let completedSessions = 0;
  if (company.station_results) {
    Object.values(company.station_results).forEach(res => {
      if (res && res !== 'blank') completedSessions++;
    });
  } else {
    completedSessions = company.completed_station_count || 0;
  }

  const active = company.active_station_count || 0;
  const total = requiredStations ?? (stations ? stations.length : 9);
  const progress = `${completedSessions + active}/${total}`;

  const isMakeupEligible = company.status === 'makeup_eligible';
  const isMakeupActive = company.status === 'makeup_active';
  const isReviewReady = company.status === 'review_ready';
  const isMakeupRelated = isMakeupEligible || isMakeupActive;

  const makeupFailures = company.makeup_failures || {};
  const failedStationIds = Object.keys(makeupFailures).map(Number);
  const hasFailures = failedStationIds.length > 0;

  const draggable = !!onDragStart && company.status === 'registered';

  let borderColor = '#e2e8f0';
  if (isReviewReady) borderColor = '#48bb78';
  else if (isMakeupActive) borderColor = '#ed8936';
  else if (isMakeupEligible) borderColor = '#fc8181';

  return (
    <div
      className={`company-card${isRecommended ? ' recommended' : ''}`}
      draggable={draggable}
      onDragStart={draggable ? onDragStart : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: draggable ? 'grab' : 'default',
        borderLeft: `4px solid ${borderColor}`,
        ...style
      }}
    >
      <div className="company-header">
        <div className="company-name">{company.name}</div>
        <div className={`status-dot status-${company.status}`} title={company.status}></div>
      </div>

      <div className="company-candidates">{candidateNames}</div>

      <div className="company-footer">
        <div className="program-name">{company.program_name}</div>
        <div className="progress-text">{progress}</div>
        {stagingEnteredAt && (
          <div className="status-timer" style={{ fontSize: '0.8em', color: '#718096', marginTop: 2 }}>
            ⏱ {stagingElapsed}
          </div>
        )}
      </div>

      {/* Review-ready: all stations passed, waiting for admin sign-off */}
      {isReviewReady && (
        <div style={{ marginTop: 8, padding: '6px 8px', background: '#f0fff4', borderRadius: 6, border: '1px solid #9ae6b4' }}>
          <div style={{ fontSize: '0.82em', fontWeight: 600, color: '#276749', marginBottom: 6 }}>
            ✓ All stations passed — ready for sign-off
          </div>
          <button
            className="btn btn-success btn-sm"
            style={{ width: '100%' }}
            onClick={onMoveToPassed}
          >
            Move to Passed
          </button>
        </div>
      )}

      {/* Makeup eligible: has failures, makeups not yet started */}
      {isMakeupEligible && (
        <div style={{ marginTop: 8, padding: '6px 8px', background: '#fff5f5', borderRadius: 6, border: '1px solid #fc8181' }}>
          <div style={{ fontSize: '0.82em', fontWeight: 600, color: '#c53030', marginBottom: 6 }}>
            Needs makeups on {failedStationIds.length} station{failedStationIds.length !== 1 ? 's' : ''}
          </div>
          <button
            className="btn btn-warning btn-sm"
            style={{ width: '100%' }}
            onClick={onBeginMakeups}
          >
            Begin Makeups
          </button>
        </div>
      )}

      {/* Makeup active: dispatching to failed stations */}
      {isMakeupActive && hasFailures && (
        <div style={{ marginTop: 8, padding: '6px 8px', background: '#fffaf0', borderRadius: 6, border: '1px solid #f6ad55' }}>
          <button
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontSize: '0.82em',
              fontWeight: 600,
              color: '#c05621',
              width: '100%',
              textAlign: 'left',
              display: 'flex',
              justifyContent: 'space-between'
            }}
            onClick={() => setMakeupExpanded(x => !x)}
          >
            <span>Makeup stations ({failedStationIds.length})</span>
            <span>{makeupExpanded ? '▲' : '▼'}</span>
          </button>

          {makeupExpanded && (
            <div style={{ marginTop: 8 }}>
              {failedStationIds.map(stId => {
                const info = makeupFailures[stId];
                return (
                  <div key={stId} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #fbd38d' }}>
                    <div style={{ fontSize: '0.8em', fontWeight: 600, color: '#744210', marginBottom: 4 }}>
                      Station {info.station_number}: {info.station_name}
                      <span style={{
                        marginLeft: 6,
                        padding: '1px 5px',
                        borderRadius: 4,
                        background: info.result === 'fail' ? '#fed7d7' : '#feebc8',
                        color: info.result === 'fail' ? '#c53030' : '#c05621',
                        fontSize: '0.85em'
                      }}>
                        {info.result === 'fail' ? 'FAIL' : 'MIXED'}
                      </span>
                    </div>
                    <ul style={{ margin: '4px 0 6px 12px', padding: 0, fontSize: '0.78em', color: '#4a5568' }}>
                      {info.failed_skills.map((sk, i) => (
                        <li key={i} style={{ marginBottom: 2 }}>
                          {sk.skill_name}
                          {sk.candidate_name && (
                            <span style={{ color: '#718096' }}> — {sk.candidate_name}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                    <button
                      className="btn btn-warning btn-sm"
                      style={{ width: '100%', fontSize: '0.8em' }}
                      onClick={() => onDispatchToStation && onDispatchToStation(stId)}
                    >
                      Dispatch to Station {info.station_number}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <button
            className="btn btn-success btn-sm"
            style={{ width: '100%', marginTop: 6 }}
            onClick={onMoveToPassed}
          >
            Move to Passed
          </button>
        </div>
      )}

      {/* Station result pips for makeup-related companies */}
      {isMakeupRelated && company.station_results && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 8 }}>
          {(stations || []).map(st => {
            const res = company.station_results[st.id] || 'blank';
            return (
              <span
                key={st.id}
                title={`Station ${st.station_number}: ${res}`}
                style={{
                  display: 'inline-block',
                  width: 18,
                  height: 18,
                  borderRadius: 3,
                  background: RESULT_BG[res] || RESULT_BG.blank,
                  color: RESULT_COLOR[res] || RESULT_COLOR.blank,
                  fontSize: '0.65em',
                  fontWeight: 700,
                  textAlign: 'center',
                  lineHeight: '18px'
                }}
              >
                {RESULT_LABEL[res] || '·'}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
